import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { DashboardShell } from "@/components/DashboardShell";
import { PARTNER_NAV } from "@/lib/nav";
import { useAuth } from "@/lib/auth";
import {
  DEFAULT_TEMPLATES,
  fetchTemplates,
  saveTemplate,
  type MessageChannel,
  type MessageTemplate,
} from "@/lib/messages";

export const Route = createFileRoute("/_authenticated/partner/message-templates")({
  head: () => ({
    meta: [
      { title: "Message templates · Landoo Partner" },
      {
        name: "description",
        content: "Edit reusable customer message templates for your business.",
      },
    ],
  }),
  component: TemplatesPage,
});

const CHANNELS: { value: MessageChannel; label: string }[] = [
  { value: "whatsapp", label: "WhatsApp" },
  { value: "sms", label: "SMS" },
  { value: "email", label: "Email" },
];

function TemplatesPage() {
  const { user } = useAuth();
  const [saved, setSaved] = useState<MessageTemplate[]>([]);
  const [busy, setBusy] = useState<string | null>(null);

  useEffect(() => {
    if (!user) return;
    fetchTemplates(user.id)
      .then(setSaved)
      .catch(() => setSaved([]));
  }, [user?.id]);

  const rows = DEFAULT_TEMPLATES.map((d) => {
    const o = saved.find((s) => s.slug === d.slug);
    return {
      slug: d.slug,
      name: o?.name ?? d.name,
      body: o?.body ?? d.body,
      channel: (o?.channel ?? "whatsapp") as MessageChannel,
    };
  });

  const [draft, setDraft] = useState<
    Record<string, { name: string; body: string; channel: MessageChannel }>
  >({});
  const valueFor = (slug: string) => draft[slug] ?? rows.find((r) => r.slug === slug)!;

  const persist = async (slug: string) => {
    if (!user) return;
    const v = valueFor(slug);
    setBusy(slug);
    try {
      await saveTemplate(user.id, { slug, name: v.name, body: v.body, channel: v.channel });
      setSaved(await fetchTemplates(user.id));
      toast.success("Template saved");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Could not save template");
    } finally {
      setBusy(null);
    }
  };

  return (
    <DashboardShell title="Message templates" role="Partner" items={PARTNER_NAV}>
      <p className="text-sm text-muted-foreground">
        Placeholders: <code>{"{{customer}}"}</code> <code>{"{{listing}}"}</code>{" "}
        <code>{"{{business}}"}</code> <code>{"{{amount}}"}</code> <code>{"{{date}}"}</code>
      </p>

      <div className="mt-4 grid gap-3 md:grid-cols-2">
        {rows.map((r) => {
          const v = valueFor(r.slug);
          return (
            <div key={r.slug} className="rounded-2xl border border-border bg-card p-4 shadow-soft">
              <input
                value={v.name}
                onChange={(e) => setDraft({ ...draft, [r.slug]: { ...v, name: e.target.value } })}
                className="w-full rounded-xl border border-border bg-background px-3 py-2 text-sm font-semibold text-brown-dark outline-none focus:border-brand"
              />
              <textarea
                rows={4}
                value={v.body}
                onChange={(e) => setDraft({ ...draft, [r.slug]: { ...v, body: e.target.value } })}
                className="mt-2 w-full rounded-xl border border-border bg-background px-3 py-2 text-sm outline-none focus:border-brand"
              />
              <div className="mt-2 flex items-center gap-2">
                <select
                  value={v.channel}
                  onChange={(e) =>
                    setDraft({
                      ...draft,
                      [r.slug]: { ...v, channel: e.target.value as MessageChannel },
                    })
                  }
                  className="rounded-xl border border-border bg-background px-3 py-2 text-xs"
                >
                  {CHANNELS.map((c) => (
                    <option key={c.value} value={c.value}>
                      {c.label}
                    </option>
                  ))}
                </select>
                <button
                  onClick={() => persist(r.slug)}
                  disabled={busy === r.slug}
                  className="ml-auto rounded-xl bg-brand px-4 py-2 text-xs font-semibold text-brown-dark shadow-soft disabled:opacity-50"
                >
                  {busy === r.slug ? "Saving…" : "Save"}
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </DashboardShell>
  );
}
